import React from "react";
import { Alert, Button, Select, Skeleton, Space, Table, Typography } from "antd";
import { FONT } from "../../styles/typography";

const { Text } = Typography;

const MONTH_OPTIONS = [3, 6, 9, 12];

/**
 * 配額排程長度的目標天數。
 *
 * 依近 12 個月的實際前置時間推得：不受配額限制的直客/旅行社中，有 11.9% 的房晚是提前
 * 120 天以上訂的，排到 180 天可涵蓋 96.4% 的需求。線上通路本身的分布 (96% 落在 120 天內)
 * 不能拿來當標準 —— 它被自己的配額截斷了，實測線上「提前 270 天以上」的訂單是 0 筆，
 * 非線上卻有 38 房晚，差別只在客人訂不訂得到。
 */
const TARGET_DAYS = 180;

// 覆蓋率的色階。0% 是「這個月完全沒開放線上訂」，與「開放得少」是不同性質的問題，
// 所以獨立一個最重的色，不跟低覆蓋率混在同一個漸層裡。
const cellStyle = (pct) => {
  if (pct === 0) return { background: "#fff1f0", color: "#cf1322", fontWeight: 600 };
  if (pct < 40) return { background: "#fff7e6", color: "#d46b08" };
  if (pct < 80) return { background: "#feffe6", color: "#7cb305" };
  return { background: "#f6ffed", color: "#389e0d" };
};

/**
 * 曝光排程 —— 未來數個月、逐房型的 OTA 配額覆蓋率。
 *
 * 與上方的曝光結構分開的理由：那裡看的是「選定期間的配置長什麼樣」，這裡看的是
 * 「從現在往後還排了多久」。後者固定從今天起算，跟使用者正在檢視哪一段無關。
 *
 * 用月粒度而非日粒度：遠期尚未設配額的日子是連續一大片，逐日列出會有數千筆而失去意義。
 */
export default function ExposureSchedule({
  data = null,
  loading = false,
  months,
  onMonthsChange,
  error = null,
  onRefresh,
}) {
  const monthSelect = (
    <Select value={months} onChange={onMonthsChange} style={{ width: 120 }} disabled={loading}>
      {MONTH_OPTIONS.map((m) => (
        <Select.Option key={m} value={m}>未來 {m} 個月</Select.Option>
      ))}
    </Select>
  );

  const controls = (
    <Space style={{ marginBottom: 12 }} wrap>
      {monthSelect}
      {onRefresh && <Button size="small" loading={loading} onClick={onRefresh}>重新整理</Button>}
    </Space>
  );

  if (error) {
    return (
      <>
        {controls}
        <Alert
          type="error"
          showIcon
          message="無法取得曝光排程"
          description={error}
          action={onRefresh ? <Button size="small" onClick={onRefresh}>重試</Button> : null}
        />
      </>
    );
  }

  if (!data) {
    return (
      <>
        {controls}
        <Skeleton active paragraph={{ rows: 6 }} />
      </>
    );
  }

  const { month_keys: monthKeys = [], rows = [], summary = {} } = data;

  const columns = [
    {
      title: "房型",
      key: "room",
      fixed: "left",
      width: 190,
      render: (_, r) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {r.nam}
          <Text type="secondary" style={{ fontSize: FONT.caption, marginLeft: 6 }}>{r.cod}</Text>
        </span>
      ),
    },
    {
      title: "配額排到",
      key: "last_quota",
      fixed: "left",
      width: 120,
      render: (_, r) =>
        r.last_quota ? (
          <span style={{ whiteSpace: "nowrap" }}>{r.last_quota}</span>
        ) : (
          <Text type="danger" strong>整段皆無</Text>
        ),
    },
    ...monthKeys.map((ym) => ({
      title: <span style={{ whiteSpace: "nowrap" }}>{ym.replace("-", "/")}</span>,
      key: ym,
      align: "center",
      width: 92,
      onCell: (row) => ({ style: cellStyle(row.cells?.[ym]?.pct ?? 0) }),
      render: (_, row) => {
        const c = row.cells?.[ym];
        if (!c) return <Text type="secondary">—</Text>;
        return (
          <span title={`${c.covered} / ${c.days} 天有配額，未售 ${c.unsold} 房晚`}>
            {c.pct}%
          </span>
        );
      },
    })),
  ];

  const hasGap = summary.empty_months > 0;
  const shortfall = TARGET_DAYS - (summary.days_ahead || 0);
  const belowTarget = shortfall > 0;

  return (
    <>
      {controls}

      <Alert
        type={belowTarget || hasGap ? "warning" : "success"}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          summary.last_quota
            ? `OTA 配額目前排到 ${summary.last_quota}，距今 ${summary.days_ahead} 天`
            : "未來這段期間完全沒有設定 OTA 配額"
        }
        description={
          <>
            <div>
              建議維持隨時往後<Text strong>至少 {TARGET_DAYS} 天</Text>
              {belowTarget ? (
                <Text strong type="danger">　目前還差 {shortfall} 天</Text>
              ) : (
                <Text strong type="success">　已達標</Text>
              )}
              <Text type="secondary">
                　(依實際訂房前置時間推得：排到 {TARGET_DAYS} 天可涵蓋 96% 的需求)
              </Text>
            </div>
            {hasGap && (
              <div style={{ marginTop: 4 }}>
                未來 {months} 個月中有 {summary.empty_months} 個月完全沒有配額，
                那些月份合計 {summary.affected_nights.toLocaleString()} 房晚在 OTA 上買不到。
              </div>
            )}
          </>
        }
      />

      <Table
        size="small"
        rowKey="cod"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: "max-content" }}
      />

      <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
        每格為該月「有設配額的天數 ÷ 當月天數」。
        <Text strong style={{ color: "#cf1322" }}> 紅色 0%</Text> 代表該房型整個月在 OTA 上都買不到；
        房型依配額最早斷掉者排在前面。
        此處的 OTA 指<Text strong>所有線上通路，含飯店官網</Text> (共用同一份 WRS 鎖控)。
      </Text>
    </>
  );
}
